import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import type { FAQ } from '../../mocks/faq';

interface FAQSectionProps {
  title?: string;
  subtitle?: string;
  faqs: FAQ[];
}

export default function FAQSection({ title = 'Frequently Asked Questions', subtitle = 'Real answers to the questions businesses ask most about commercial cleaning and facility services.', faqs }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div ref={ref} className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-5">Voice Search Optimized</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-5 leading-tight">{title}</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
        </div>

        <div className={`space-y-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '200ms' }}>
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-2xl border overflow-hidden transition-all duration-300 ${openIndex === i ? 'border-[#C70532]/30' : 'border-gray-100'}`}
            >
              <button
                className={`w-full flex items-center gap-5 px-7 py-6 text-left cursor-pointer transition-colors ${openIndex === i ? 'bg-[#03143A]' : 'bg-[#FAF8F5] hover:bg-gray-50'}`}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openIndex === i ? 'bg-[#C70532]' : 'bg-[#C70532]/15'}`}>
                  <span className={`text-xs font-black transition-colors ${openIndex === i ? 'text-white' : 'text-[#C70532]'}`}>{i + 1}</span>
                </div>
                <h3 className={`font-bold text-base flex-1 pr-4 transition-colors leading-snug ${openIndex === i ? 'text-white' : 'text-[#03143A]'}`}>
                  {faq.question}
                </h3>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${openIndex === i ? 'bg-white/20 rotate-180' : 'bg-white border border-gray-200'}`}>
                  <i className={`ri-arrow-down-s-line transition-colors ${openIndex === i ? 'text-white' : 'text-[#03143A]'}`} />
                </div>
              </button>
              {openIndex === i && (
                <div className="bg-white px-7 py-6 pl-[5.5rem] border-t border-gray-100">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
